import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { BreakpointObserverService, HeaderMenuService, NavigationService } from '@core';
import { AccessibleClickDirective, FocusOutsideDirective } from 'libs/shared/src/lib/directives';
import { INavigationTree } from '../../interfaces';

@Component({
  selector: 'lib-navigation-links-group',
  templateUrl: './navigation-links-group.component.html',
  styleUrls: ['./navigation-links-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AccessibleClickDirective, FocusOutsideDirective],
})
export class NavigationLinksGroupComponent {
  readonly navigationTree = input.required<INavigationTree[] | null>();
  readonly headerTitle = input.required<string>();
  readonly parent = input<INavigationTree | null>(null);

  readonly closeEvent = output<void>();
  readonly focusOutsideEvent = output<void>();
  readonly backEvent = output<void>();
  readonly shopAllEvent = output<void>();

  readonly isMobileView = inject(BreakpointObserverService).isTabletOrLower;

  readonly shopAllAction = viewChild<ElementRef>('shopAllAction');
  readonly APPLE_BRAND_ID: number = 1032;
  readonly APPLE_CATEGORY_ID: number = 60001;

  readonly activeTreeItem = signal<INavigationTree | null | undefined>(undefined);
  readonly isPhone = inject(BreakpointObserverService).isTabletOrLower;

  private navigationService = inject(NavigationService);
  private headerMenuService = inject(HeaderMenuService);

  constructor() {
    effect(() => {
      this.navigationTree();
      untracked(() => {
        this.activeTreeItem.set(undefined);
        this.shopAllAction()?.nativeElement.focus();
      });
    });
  }

  readonly processedNavigationTree = computed(() => {
    const rawTree = this.navigationTree();
    if (!rawTree) return null;

    return rawTree.map((category) => {
      // For Apple subcategories (when parent is Apple), remove any deeper nesting
      if (this.parent()?.id === this.APPLE_CATEGORY_ID) {
        return {
          ...category,
          group: undefined, // Apple subcategories cannot have further dropdowns
        };
      }
      return category;
    });
  });

  readonly vm = computed(() => ({
    navigationTree: this.processedNavigationTree(),
    activeTreeItem: this.activeTreeItem(),
    isPhone: this.isPhone(),
    headerTitle: this.headerTitle(),
    isActiveTreeItemGroup: !!this.activeTreeItem()?.group?.length,
  }));

  onBack(): void {
    this.onSelectActiveTreeItem(null);
  }

  onShopAllClick(): void {
    const parent = this.parent();
    // For Apple categories, handle navigation directly here
    if (parent?.id === this.APPLE_CATEGORY_ID) {
      this.navigationService.goToBrandStore(this.APPLE_BRAND_ID);
      this.closeEvent.emit();
      return;
    }

    // For other categories, emit the event to parent component
    this.shopAllEvent.emit();
  }

  clickNavItem(item: INavigationTree): void {
    const parent = this.parent();

    // Handle Apple main category
    if (item.id === this.APPLE_CATEGORY_ID) {
      this.navigationService.goToBrandStore(this.APPLE_BRAND_ID);
      this.closeEvent.emit();
      return;
    }

    // Handle Apple subcategories using centralized logic
    if (parent?.id === this.APPLE_CATEGORY_ID) {
      this.headerMenuService.navigateToAppleCategory(item.id, this.navigationService, [parent, item]);
      this.closeEvent.emit();
      return;
    }

    // Handle other categories
    const navigationPath: INavigationTree[] = parent ? [parent, item] : [item];
    this.navigationService.goToCategoryPage(item.id, { data: JSON.stringify(navigationPath) });
    this.closeEvent.emit();
  }

  onSelectActiveTreeItem(activeTreeItem: INavigationTree | null): void {
    const parent = this.parent();
    if (parent?.id === this.APPLE_CATEGORY_ID) {
      return;
    }
    this.activeTreeItem.set(activeTreeItem);
  }

  onMouseEnterItem(item: INavigationTree): void {
    if (this.parent()?.id === this.APPLE_CATEGORY_ID) {
      return;
    }

    this.onSelectActiveTreeItem(item);
  }

  onMouseEnterShopAll(): void {
    if (this.parent()?.id === this.APPLE_CATEGORY_ID) {
      return;
    }

    this.onSelectActiveTreeItem(null);
  }
}
